import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';

import { AppContext } from '../../../../context';
// import { translate as t } from 'hooks/useTranslations';
import useOrganizationRoles from 'views/App/Portal/LexID/Roles/_data_sources/useRoles';
import useGroupRoles from 'views/App/Portal/LexID/Groups/_data_sources/useGroupRoles';
import useManageGroupRole from 'views/App/Portal/LexID/Groups/_data_sources/useManageGroupRole';
import { Role } from 'views/App/Portal/LexID/Roles/_types/role';
import Loading from 'views/App/Portal/Loading';
import errorHelper from 'helpers/errorHelper';
import TruncatedText from '_lex/_atoms/TruncatedText';

export default function Roles() {
  const { currentOrganizationId } = useContext(AppContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const queryClient = useQueryClient();
  //
  const { id } = useParams();
  const rolesQuery = useOrganizationRoles(currentOrganizationId || '');
  const userRolesQuery = useGroupRoles(currentOrganizationId || '', id || '');
  const { addRole, removeRole } = useManageGroupRole();

  const roles = React.useMemo(
    () => (rolesQuery.data && rolesQuery.data.pages ? rolesQuery.data.pages.flatMap((x: any) => x.content) : []),
    [rolesQuery.data]
  );

  const userRoles = React.useMemo(
    () => (userRolesQuery.data && userRolesQuery.data.roles ? userRolesQuery.data.roles : []),
    [userRolesQuery.data]
  );

  const handleRoleChange = (role: Role, isEnabled: boolean) => {
    setIsSaving(true);
    if (isEnabled) {
      addRole(currentOrganizationId || '', id || '', role.id || '', () => {
        setIsSaving(false);
        queryClient.invalidateQueries(['group_roles', currentOrganizationId, id]);
      });
    } else {
      removeRole(currentOrganizationId || '', id || '', role.id || '', () => {
        setIsSaving(false);
        queryClient.invalidateQueries(['group_roles', currentOrganizationId, id]);
      });
    }
  };
  if (rolesQuery.isError) {
    return errorHelper.renderErrors(rolesQuery);
  }

  if (userRolesQuery.isError) {
    return errorHelper.renderErrors(userRolesQuery);
  }

  return rolesQuery.isLoading || rolesQuery.isFetching || userRolesQuery.isLoading || userRolesQuery.isFetching ? (
    <Loading />
  ) : (
    <Stack spacing={3}>
      {roles.map((role) => (
        <FormControlLabel
          key={role.id}
          sx={{
            opacity: isSaving ? 0.5 : 1,
            pointerEvents: isSaving ? 'none' : 'auto',
            width: 'fit-content',
          }}
          control={
            <Switch
              id={role.id}
              checked={userRoles.map((x) => x?.id).includes(role?.id)}
              onChange={({ target }) => handleRoleChange(role, target.checked)}
            />
          }
          label={
            <Stack>
              <TruncatedText
                text={role.name}
                maxWidth={420}
                typographyProps={{ sx: { fontWeight: 600 } }}
              />
              {role.description?.length > 0 && <Typography variant='caption'>{role.description}</Typography>}
            </Stack>
          }
        />
      ))}
    </Stack>
  );
}
